(() => {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initReveals(root = document) {
    const items = [...root.querySelectorAll('[data-wj-reveal]:not([data-wj-ready])')];
    if (!items.length) return;
    items.forEach((item, index) => {
      item.dataset.wjReady = 'true';
      item.style.setProperty('--wj-order', String(index % 8));
    });
    if (reduced.matches || !('IntersectionObserver' in window)) {
      items.forEach(item => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        entry.target.classList.toggle('is-visible', entry.isIntersecting);
      });
    }, {rootMargin: '10% 0px 12%', threshold: .04});
    items.forEach(item => observer.observe(item));
  }

  function initReadingProgress(root = document) {
    const article = root.querySelector('[data-wj-article]');
    const bar = root.querySelector('[data-wj-progress]');
    if (!article || !bar || article.dataset.wjProgressReady) return;
    article.dataset.wjProgressReady = 'true';
    let ticking = false;
    const update = () => {
      const rect = article.getBoundingClientRect();
      const total = Math.max(1, article.offsetHeight - window.innerHeight * .45);
      const travelled = Math.min(total, Math.max(0, -rect.top + window.innerHeight * .18));
      bar.style.transform = `scaleX(${travelled / total})`;
      ticking = false;
    };
    const request = () => {
      if (!ticking) requestAnimationFrame(update);
      ticking = true;
    };
    addEventListener('scroll', request, {passive: true});
    addEventListener('resize', request, {passive: true});
    request();
  }

  function initRelatedCarousels(root = document) {
    root.querySelectorAll('[data-wj-related-carousel]:not([data-wj-carousel-ready])').forEach(carousel => {
      const slides = [...carousel.querySelectorAll('[data-wj-related-slide]')];
      if (!slides.length) return;
      carousel.dataset.wjCarouselReady = 'true';
      const interval = Math.max(3000, Number(carousel.dataset.wjInterval) || 5000);
      let active = Math.max(0, slides.findIndex(slide => slide.classList.contains('is-active')));
      let timer = null;

      const show = index => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === active;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
      };
      const stop = () => { if (timer) clearInterval(timer); timer = null; };
      const start = () => {
        stop();
        if (!reduced.matches && slides.length > 1) timer = setInterval(() => show(active + 1), interval);
      };

      carousel.addEventListener('pointerenter', stop);
      carousel.addEventListener('pointerleave', start);
      carousel.addEventListener('focusin', stop);
      carousel.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
      show(active);
      start();
    });
  }

  function initAdaptiveMastheads(root = document) {
    root.querySelectorAll('[data-wj-adaptive-masthead]:not([data-wj-contrast-ready])').forEach(masthead => {
      masthead.dataset.wjContrastReady = 'true';
      const image = masthead.querySelector('img');
      if (!image) return;
      const analyse = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = 48; canvas.height = 32;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          const pixels = context.getImageData(0, 0, 34, 32).data;
          const values = [];
          const linear = value => value <= .04045 ? value / 12.92 : ((value + .055) / 1.055) ** 2.4;
          for (let index = 0; index < pixels.length; index += 4) {
            if (pixels[index + 3] < 180) continue;
            values.push(.2126 * linear(pixels[index] / 255) + .7152 * linear(pixels[index + 1] / 255) + .0722 * linear(pixels[index + 2] / 255));
          }
          if (!values.length) return;
          values.sort((a, b) => a - b);
          const average = values.reduce((sum, value) => sum + value, 0) / values.length;
          const low = values[Math.floor(values.length * .18)];
          const high = values[Math.floor(values.length * .82)];
          const darkText = average > .56 && low > .26;
          if (darkText) {
            const alpha = Math.min(.82, Math.max(.42, (.58 - low) / Math.max(.01, 1 - low)));
            masthead.dataset.wjContrast = 'dark';
            masthead.style.setProperty('--wj-hero-fg', '#102019');
            masthead.style.setProperty('--wj-hero-muted', 'rgba(16,32,25,.76)');
            masthead.style.setProperty('--wj-hero-veil-strong', `rgba(244,239,229,${alpha.toFixed(2)})`);
            masthead.style.setProperty('--wj-hero-veil-mid', `rgba(244,239,229,${Math.max(.28, alpha - .16).toFixed(2)})`);
            masthead.style.setProperty('--wj-hero-veil-soft', 'rgba(244,239,229,.08)');
            masthead.style.setProperty('--wj-hero-veil-bottom', `rgba(244,239,229,${Math.max(.25, alpha - .12).toFixed(2)})`);
          } else {
            const alpha = Math.min(.88, Math.max(.48, 1 - (.16 / Math.max(.17, high))));
            masthead.dataset.wjContrast = 'light';
            masthead.style.setProperty('--wj-hero-fg', '#fff');
            masthead.style.setProperty('--wj-hero-muted', 'rgba(255,255,255,.82)');
            masthead.style.setProperty('--wj-hero-veil-strong', `rgba(8,22,17,${alpha.toFixed(2)})`);
            masthead.style.setProperty('--wj-hero-veil-mid', `rgba(8,22,17,${Math.max(.48, alpha - .20).toFixed(2)})`);
            masthead.style.setProperty('--wj-hero-veil-soft', 'rgba(8,22,17,.14)');
            masthead.style.setProperty('--wj-hero-veil-bottom', `rgba(8,22,17,${Math.max(.52, alpha - .12).toFixed(2)})`);
          }
        } catch (_) { masthead.dataset.wjContrast = 'light'; }
      };
      if (image.complete && image.naturalWidth) analyse();
      else image.addEventListener('load', analyse, { once: true });
    });
  }

  function init(root = document) {
    initReveals(root);
    initReadingProgress(root);
    initRelatedCarousels(root);
    initAdaptiveMastheads(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true});
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
